import { Combobox } from "@base-ui/react/combobox"
import classNames from "classnames"
import PropTypes from "prop-types"
import React, { useCallback } from "react"

// Distance from the bottom of the list (in px) at which onLoadMore fires.
const LOAD_MORE_THRESHOLD_PX = 80

const FormCombobox = ({
  id,
  items = [],
  filteredItems,
  value,
  onValueChange,
  inputValue,
  onInputValueChange,
  itemToStringLabel,
  itemToStringValue,
  isItemEqualToValue,
  renderOption,
  placeholder,
  disabled = false,
  autoFocus = false,
  hasErrors = false,
  isLoading = false,
  noResultsContent,
  onLoadMore,
}) => {
  const handleScroll = useCallback(
    (event) => {
      if (!onLoadMore) return
      const { scrollHeight, scrollTop, clientHeight } = event.currentTarget
      if (scrollHeight - scrollTop - clientHeight <= LOAD_MORE_THRESHOLD_PX) {
        onLoadMore()
      }
    },
    [onLoadMore]
  )

  return (
    <Combobox.Root
      items={items}
      filteredItems={filteredItems}
      value={value}
      onValueChange={onValueChange}
      inputValue={inputValue}
      onInputValueChange={onInputValueChange}
      itemToStringLabel={itemToStringLabel}
      itemToStringValue={itemToStringValue}
      isItemEqualToValue={isItemEqualToValue}
      disabled={disabled}
    >
      <div className="relative">
        <Combobox.Input
          id={id}
          placeholder={placeholder}
          autoFocus={autoFocus}
          autoComplete="off"
          className={classNames(
            "h-10 w-full rounded border px-2 py-3 pr-8 outline-none focus:border-blue-600",
            {
              "border-red-600": hasErrors,
              "cursor-not-allowed bg-gray-100 text-gray-500": disabled,
            }
          )}
        />
        {isLoading ? (
          <span
            data-testid="form-combobox-spinner"
            className="fa-solid fa-spinner-third fa-spin absolute bottom-0 right-3 top-0 flex items-center text-gray-400"
          />
        ) : (
          <Combobox.Icon className="pointer-events-none absolute bottom-0 right-3 top-0 flex items-center">
            <i className="fa-solid fa-chevron-down text-xs text-gray-600" />
          </Combobox.Icon>
        )}
      </div>
      <Combobox.Portal>
        <Combobox.Positioner
          side="bottom"
          sideOffset={4}
          align="start"
          className="outline-hidden z-modal-stacked"
        >
          <Combobox.Popup
            className="overflow-hidden rounded bg-white shadow-lg ring-1 ring-black ring-opacity-5"
            style={{ minWidth: "var(--anchor-width)" }}
          >
            <Combobox.Empty>
              <div className="px-2 py-4 text-center text-sm text-gray-500">
                {noResultsContent ?? "No results found"}
              </div>
            </Combobox.Empty>
            <Combobox.List
              className="max-h-60 overflow-auto py-1"
              onScroll={onLoadMore ? handleScroll : undefined}
            >
              {(item) => (
                <Combobox.Item
                  key={item?.id ?? itemToStringValue?.(item) ?? item}
                  value={item}
                  className="flex cursor-pointer items-center px-2 py-2 text-sm text-gray-800 data-[highlighted]:bg-blue-100 data-[highlighted]:outline-none"
                >
                  {renderOption
                    ? renderOption({ option: item })
                    : itemToStringLabel?.(item) ?? String(item)}
                </Combobox.Item>
              )}
            </Combobox.List>
          </Combobox.Popup>
        </Combobox.Positioner>
      </Combobox.Portal>
    </Combobox.Root>
  )
}

FormCombobox.propTypes = {
  id: PropTypes.string,
  items: PropTypes.array,
  filteredItems: PropTypes.array,
  value: PropTypes.any,
  onValueChange: PropTypes.func,
  inputValue: PropTypes.string,
  onInputValueChange: PropTypes.func,
  itemToStringLabel: PropTypes.func,
  itemToStringValue: PropTypes.func,
  isItemEqualToValue: PropTypes.func,
  renderOption: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  hasErrors: PropTypes.bool,
  isLoading: PropTypes.bool,
  noResultsContent: PropTypes.node,
  onLoadMore: PropTypes.func,
}

export default FormCombobox
